import type { Translations } from '../types';

export const de: Translations = {
  common: {
    home: 'Startseite',
    github: 'GitHub',
    copy: 'Kopieren',
    clear: 'Löschen',
    input: 'Eingabe',
    output: 'Ausgabe',
    encode: 'Kodieren',
    decode: 'Dekodieren',
    generate: 'Generieren',
    convert: 'Konvertieren',
    result: 'Ergebnis',
    copiedToClipboard: 'In die Zwischenablage kopiert!',
    failedToCopy: 'Kopieren fehlgeschlagen',
    language: 'Sprache',
  },
  seo: {
    siteDescription: 'Kostenlose, datenschutzorientierte Entwickler-Tools-Sammlung. JSON-Formatierer, Base64-Encoder/Decoder, UUID-Generator, Hash-Generator, URL-Encoder, Zeitstempel-Konverter und Farbkonverter. Alle Tools funktionieren offline in Ihrem Browser.',
    keywords: [
      'Entwickler-Tools',
      'JSON-Formatierer',
      'Base64-Encoder',
      'UUID-Generator',
      'Hash-Generator',
      'URL-Encoder',
      'Zeitstempel-Konverter',
      'Farbkonverter',
      'Online-Tools',
      'Kostenlose Tools',
      'Datenschutz',
      'Offline-Tools',
    ],
    ogTitle: 'GetDev.Tools - Kostenlose datenschutzorientierte Entwickler-Tools',
    ogDescription: 'Sammlung kostenloser Entwickler-Tools, die offline in Ihrem Browser funktionieren. JSON-Formatierer, Base64, UUID, Hash, URL-Encoder und mehr.',
  },
  home: {
    title: 'Entwickler-Tools-Sammlung',
    subtitle: 'Kostenlose, datenschutzfreundliche Tools für Entwickler. Alle Verarbeitung erfolgt in Ihrem Browser.',
    whyTitle: 'Warum GetDev.Tools?',
    privacyTitle: '🔒 Datenschutz zuerst',
    privacyDesc: 'Alle Verarbeitung erfolgt in Ihrem Browser. Ihre Daten verlassen niemals Ihr Gerät.',
    fastTitle: '⚡ Blitzschnell',
    fastDesc: 'Keine Server-Roundtrips. Alles läuft lokal für sofortige Ergebnisse.',
    freeTitle: '🆓 Für immer kostenlos',
    freeDesc: 'Open Source und kostenlos. Keine Registrierung oder Zahlung erforderlich.',
    footer: '© 2025 GetDev.Tools - Datenschutzfreundliche Entwickler-Tools',
    footerSubtitle: 'Alle Verarbeitung erfolgt in Ihrem Browser',
  },
  tools: {
    jsonFormatter: {
      name: 'JSON-Formatierer',
      description: 'JSON-Daten formatieren, validieren und verschönern',
      format: 'Formatieren',
      minify: 'Minimieren',
      validate: 'Validieren',
      error: 'Fehler',
    },
    base64: {
      name: 'Base64-Encoder/Decoder',
      description: 'Base64-Strings kodieren und dekodieren',
      encodeButton: 'Kodieren',
      decodeButton: 'Dekodieren',
    },
    uuid: {
      name: 'UUID-Generator',
      description: 'RFC 4122-konforme UUID v4-Identifikatoren generieren',
      numberOfUuids: 'Anzahl der zu generierenden UUIDs',
      generatedUuids: 'Generierte UUIDs',
      copyAll: 'Alle kopieren',
      aboutTitle: 'Über UUID v4',
      aboutDesc: 'UUID (Universally Unique Identifier) v4 sind zufällig generierte 128-Bit-Identifikatoren. Die Kollisionswahrscheinlichkeit ist extrem gering, was sie für verteilte Systeme ohne zentrale Koordination geeignet macht.',
    },
    hash: {
      name: 'Hash-Generator',
      description: 'SHA-1, SHA-256, SHA-512 Hashes generieren',
      inputText: 'Eingabetext',
      securityNotice: '⚠️ Sicherheitshinweis',
      securityDesc: 'SHA-1 ist aus Sicherheitsgründen veraltet. Verwenden Sie SHA-256 oder SHA-512 für kryptografische Anwendungen. MD5 ist in modernen Browsern aufgrund von Sicherheitslücken nicht verfügbar.',
    },
    urlEncoder: {
      name: 'URL-Encoder/Decoder',
      description: 'URL-Strings kodieren und dekodieren',
      encodeUrl: 'URL kodieren',
      encodeComponent: 'Komponente kodieren',
      encoded: 'Kodiert',
      decoded: 'Dekodiert',
      differenceTitle: '💡 Unterschied',
      encodeUriDesc: 'encodeURI: Kodiert eine vollständige URI (kodiert nicht: ; , / ? : @ & = + $ #)',
      encodeComponentDesc: 'encodeURIComponent: Kodiert URI-Komponente (kodiert alle Sonderzeichen)',
      examples: 'Beispiele',
    },
    timestamp: {
      name: 'Zeitstempel-Konverter',
      description: 'Zwischen Unix-Zeitstempeln und lesbaren Daten konvertieren',
      currentTime: 'Aktuelle Zeit',
      unixMs: 'Unix-Zeitstempel (ms)',
      unixS: 'Unix-Zeitstempel (s)',
      toDate: 'Zeitstempel → Datum',
      toTimestamp: 'Datum → Zeitstempel',
      useNow: 'Jetzt verwenden',
      aboutTitle: '💡 Über Unix-Zeitstempel',
      aboutDesc1: '• Unix-Zeitstempel = Sekunden seit 1. Januar 1970 00:00:00 UTC (Epoche)',
      aboutDesc2: '• 10 Ziffern = Sekunden (z.B. 1609459200)',
      aboutDesc3: '• 13 Ziffern = Millisekunden (z.B. 1609459200000)',
      aboutDesc4: '• Unterstützte Datumsformate: ISO 8601, RFC 2822 oder natürliche Sprache',
    },
    color: {
      name: 'Farbkonverter',
      description: 'Zwischen HEX-, RGB- und HSL-Formaten konvertieren',
      pickColor: 'Farbe wählen',
      formatGuide: '💡 Farbformat-Leitfaden',
      hexDesc: 'HEX: Hexadezimale Notation (#RRGGBB)',
      rgbDesc: 'RGB: Rot-, Grün-, Blauwerte (0-255)',
      hslDesc: 'HSL: Farbton (0-360°), Sättigung (0-100%), Helligkeit (0-100%)',
      alphaDesc: 'RGBA/HSLA: Wie RGB/HSL mit Alpha-Kanal (Deckkraft)',
    },
    jwt: { name: 'JWT-Decoder', description: 'JSON Web Tokens dekodieren und inspizieren', inputToken: 'JWT-Token', header: 'Header', payload: 'Payload', signature: 'Signatur (Base64)', claims: 'Standard-Claims', invalidFormat: 'Ungültiges JWT-Format', invalidToken: 'Ungültiges JWT-Token', securityNotice: 'Sicherheitshinweis', securityDesc: 'Dieses Tool dekodiert nur JWT-Tokens', aboutTitle: 'Über JWT', aboutDesc1: 'JWT = JSON Web Token', aboutDesc2: 'Format: Header.Payload.Signatur', aboutDesc3: 'Für sichere Authentifizierung verwendet' },
    diff: { name: 'Text-Diff', description: 'Zwei Textblöcke vergleichen', originalText: 'Originaltext', modifiedText: 'Geänderter Text', sideBySide: 'Nebeneinander', unified: 'Vereint', noDifference: 'Keine Unterschiede' },
    markdown: { name: 'Markdown-Vorschau', description: 'Markdown in Echtzeit schreiben und anzeigen', editor: 'Editor', preview: 'Vorschau', supportedFeatures: 'Unterstützt: Überschriften, Listen, Links, Bilder, Codeblöcke, Tabellen usw.' },
    regex: { name: 'Regex-Tester', description: 'Reguläre Ausdrücke testen', pattern: 'Regulärer Ausdruck', flags: 'Flags', testString: 'Testzeichenfolge', matches: 'Treffer', noMatches: 'Keine Treffer', invalidRegex: 'Ungültiger Regex', globalFlag: 'Global (g)', caseInsensitive: 'Groß-/Kleinschreibung ignorieren (i)', multiline: 'Mehrzeilig (m)', dotAll: 'Punkt entspricht Zeilenumbruch (s)' },
    qrCode: { name: 'QR-Code-Generator', description: 'QR-Codes für URLs, Text, WiFi und mehr generieren', inputData: 'Eingabedaten', qrCodeImage: 'QR-Code-Bild', size: 'Größe (Pixel)', errorCorrection: 'Fehlerkorrektur', errorLow: 'Niedrig (~7%)', errorMedium: 'Mittel (~15%)', errorQuartile: 'Hoch (~25%)', errorHigh: 'Sehr hoch (~30%)', templates: 'Schnellvorlagen', templateUrl: 'Website-URL', templateEmail: 'E-Mail', templatePhone: 'Telefonnummer', templateSms: 'SMS', templateWifi: 'WiFi-Netzwerk', templateVcard: 'Kontaktkarte (vCard)', privacyNotice: '🔒 Datenschutzhinweis', privacyDesc: 'QR-Codes werden mit einer externen API (qrserver.com) generiert. Ihre Daten werden an deren Server gesendet.' },
    lorem: { name: 'Lorem-Ipsum-Generator', description: 'Platzhaltertext für Designs und Mockups generieren', generateType: 'Generierungstyp', paragraphs: 'Absätze', sentences: 'Sätze', words: 'Wörter', count: 'Anzahl', startWithLorem: 'Mit "Lorem ipsum" beginnen', generatedText: 'Generierter Text', aboutTitle: '💡 Über Lorem Ipsum', aboutDesc: 'Lorem Ipsum ist ein Platzhaltertext, der häufig in Design und Verlagswesen verwendet wird.' },
    htmlEntity: { name: 'HTML-Entity-Encoder', description: 'HTML-Entitäten codieren und decodieren', inputText: 'Eingabetext', encodeBasic: 'Codieren (Basis)', encodeAll: 'Alles codieren', decode: 'Decodieren', commonEntities: 'Häufige HTML-Entitäten', character: 'Zeichen', entity: 'Entität', description2: 'Beschreibung', useCases: '💡 Anwendungsfälle', useCase1: 'Basis-Codierung: Codiert nur wesentliche Zeichen (<, >, &, ", \')', useCase2: 'Alles codieren: Codiert alle Sonderzeichen für maximale Sicherheit', useCase3: 'Decodieren: Konvertiert HTML-Entitäten zurück in normale Zeichen' },
  },
};

